import React from 'react';

import AboutMe from '../components/aboutme';
import Contact from '../components/contact';
import Footer from '../components/footer';
import Navbar from '../components/navbar';
import Projets from '../components/projects';
import Skills from '../components/skills';
import HeroSection from '../components/HeroSection';
import Testimonials from '../components/testimonials';
import ConsultCTA from '../components/consultCTA';
import Certificates from '../components/certificates';
import Faq from '../components/faq';
import { useGsapScrollFadeUp } from '../hooks/useGsapScrollFadeUp';

import ClubDelfinesVideo from '../assets/videos/Clubdelfines.mp4';
import TrabookVideo from '../assets/videos/Trabook.mp4';
import JuanValdezVideo from '../assets/videos/JuanValdez.mp4';
import SwipePayVideo from '../assets/videos/SwipePay.mp4';
import ReelTalkVideo from '../assets/videos/reeltalk.mp4';

const Home = () => {
  useGsapScrollFadeUp();

  const stats = [
    { value: '5+', label: 'Years building products' },
    { value: '20+', label: 'Projects shipped' },
    { value: '4', label: 'Stacks mastered' },
    { value: '100%', label: 'Commitment to quality' },
  ];

  const projects = [
    {
      id: '01',
      nombre: 'Delfines Swim Club',
      category: 'Mobile · Flutter',
      tags: ['UI', 'UX', 'Flutter'],
      video: ClubDelfinesVideo,
      desc: 'Swim club management app with member tracking, schedules and a clean, friendly interface designed end-to-end.',
    },
    {
      id: '02',
      nombre: 'Trabook Web',
      category: 'Fullstack · NodeJS',
      tags: ['Fullstack', 'Design', 'NodeJS'],
      video: TrabookVideo,
      desc: 'Booking platform built fullstack with a Node backend, focused on a smooth reservation flow and scalable architecture.',
    },
    {
      id: '03',
      nombre: 'Redesign Juan Valdez App',
      category: 'Product Design · Figma',
      tags: ['UI', 'Figma'],
      video: JuanValdezVideo,
      desc: 'A UI/UX redesign concept for the Juan Valdez app, rethinking ordering and loyalty for a more delightful experience.',
    },
    {
      id: '04',
      nombre: 'SwipePay App',
      category: 'Product Design · Fintech',
      tags: ['UI', 'UX', 'Figma'],
      video: SwipePayVideo,
      desc: 'Fintech payments concept with a frictionless swipe-to-pay interaction and a bold, trustworthy visual language.',
    },
    {
      id: '05',
      nombre: 'ReelTalk App',
      category: 'Mobile · Firebase',
      tags: ['Mobile', 'Design', 'Firebase'],
      video: ReelTalkVideo,
      desc: 'Social mobile app backed by Firebase, combining real-time data with a playful, content-first design.',
    },
  ];

  const skillSector = [
    {
      id: 1,
      sector: 'Frontend',
      skills: [
        { id: 1, name: 'HTML & CSS3' },
        { id: 2, name: 'ReactJS' },
        { id: 3, name: 'VueJS' },
        { id: 4, name: 'Flutter' },
        { id: 5, name: 'Figma' },
      ],
    },
    {
      id: 2,
      sector: 'Backend',
      skills: [
        { id: 1, name: 'Javascript' },
        { id: 2, name: 'Python' },
        { id: 3, name: 'SQL' },
        { id: 4, name: 'NodeJS' },
        { id: 5, name: 'Azure Cloud' },
      ],
    },
    {
      id: 3,
      sector: 'Design',
      skills: [
        { id: 1, name: 'Web Design' },
        { id: 2, name: 'Mobile Design' },
        { id: 3, name: 'Design UI/UX' },
        { id: 4, name: 'Wireframing' },
        { id: 5, name: 'User Research' },
      ],
    },
    {
      id: 4,
      sector: 'Soft Skills',
      skills: [
        { id: 1, name: 'Leadership' },
        { id: 2, name: 'Effective communication' },
        { id: 3, name: 'Teamwork' },
        { id: 4, name: 'Commitment' },
        { id: 5, name: 'Agile development' },
      ],
    },
  ];

  const testimonials = [
    {
      quote:
        'Santiago took our idea from a rough sketch to a deployed product faster than we expected, without ever cutting corners on quality. The kind of engineer you want on every project.',
      name: 'Laura Restrepo',
      role: 'Product Manager · Fintech',
      rating: '5/5',
    },
    {
      quote:
        'A strong technical lead and a genuinely clear communicator. He kept the whole team aligned and the architecture clean from kickoff to launch.',
      name: 'Daniel Cárdenas',
      role: 'CTO · SaaS Startup',
      rating: '5/5',
    },
    {
      quote:
        'The UI/UX work was outstanding — polished, intuitive and exactly what our users needed. Conversions went up the week we shipped it.',
      name: 'María José Gómez',
      role: 'Founder · E-commerce',
      rating: '5/5',
    },
    {
      quote:
        'Reliable, proactive and obsessive about the details. Delivered on time, communicated risks early, and was a real pleasure to work with.',
      name: 'Andrés Villa',
      role: 'Engineering Manager · Logistics',
      rating: '5/5',
    },
    {
      quote:
        'We hired Santiago for one module and ended up trusting him with the core of the platform. That says everything about the quality of his work.',
      name: 'Camila Ortiz',
      role: 'Operations Director · HealthTech',
      rating: '5/5',
    },
  ];

  const certificates = [
    { title: 'Azure AI Associate Engineer', code: 'AI-102', issuer: 'Microsoft', year: '2025' },
    { title: 'Azure Fundamentals', code: 'AZ-900', issuer: 'Microsoft', year: '2025' },
    { title: 'Diseño UI/UX Avanzado', code: 'UI/UX', issuer: 'Coderhouse', year: '2025' },
    { title: 'Desarrollador FullStack', code: 'Fullstack', issuer: 'PeakU', year: '2024' },
    { title: 'Desarrollador Front-End React', code: 'React', issuer: 'Alura Latam', year: '2023' },
  ];

  const faq = [
    {
      q: 'What kind of projects do you take on?',
      a: 'Web and mobile products end-to-end: frontend, backend/APIs, cloud and UI/UX. From MVPs to scaling existing systems.',
    },
    {
      q: 'What is your tech stack?',
      a: 'React/Vue and Flutter on the frontend; Node, Python and SQL on the backend; Azure/Firebase for cloud and deployment.',
    },
    {
      q: 'How do we start working together?',
      a: 'Reach out through the contact form with a short brief. We schedule a call, scope the work and agree on milestones.',
    },
    {
      q: 'Do you work with teams or solo?',
      a: 'Both. I can lead or join a team — I have experience driving technical direction and collaborating in agile setups.',
    },
    {
      q: 'How do you handle timelines?',
      a: 'Milestone-based with regular check-ins, so progress is visible and priorities can adjust without surprises.',
    },
  ];

  const redes = [
    { id: 1, icon: 'fa-brands fa-linkedin', link: 'https://www.linkedin.com/in/santiago-estrada-dev/' },
    { id: 2, icon: 'fa-brands fa-github', link: 'https://github.com/santiago01est' },
    { id: 3, icon: 'fa-brands fa-twitter', link: 'https://twitter.com/Santiago01Est' },
    { id: 4, icon: 'fa-brands fa-instagram', link: 'https://www.instagram.com/santiago.estrada01/' },
    { id: 5, icon: 'fa-brands fa-behance', link: 'https://www.behance.net/santiagoestradaUI' },
  ];

  return (
    <main>
      <Navbar />
      <section id="home">
        <HeroSection stats={stats} />
      </section>
      <section id="expertise">
        <Skills skills={skillSector} />
      </section>
      <section id="about">
        <AboutMe />
      </section>
      <section id="testimonials">
        <Testimonials testimonials={testimonials} />
      </section>
      <section>
        <ConsultCTA />
      </section>
      <section id="work">
        <Projets projects={projects} />
      </section>
      <section id="certificates">
        <Certificates certificates={certificates} />
      </section>
      <section id="faq">
        <Faq faq={faq} />
      </section>
      <section id="contact">
        <Contact />
      </section>
      <section id="footer">
        <Footer followMe={redes} />
      </section>
    </main>
  );
};

export default Home;
